import React, { useState } from 'react';

import { toast } from 'material-react-toastify';

import { useContentViewStore } from '@/lib/store/contentViewStore';
import { ExploreBlockType } from '@addmile/backend-client';

import { useContentQueries } from '../services/ContentManagerService';
import CollectionBlocksView from './CollectionBlocksView';
import ConfirmModal from './ConfirmModal/ConfirmModal';
import ContentEditorView from './ContentEditorView';
import ContentListView from './ContentListView';
import CopyBlockModal from './CopyBlockModal/CopyBlockModal';
import CreateCollectionView from './CreateCollectionView';
import EditCollectionView from './EditCollectionView';

export const ContentManager: React.FC = () => {
  const {
    viewMode,
    editingSlug,
    draftType,
    editCollectionSlug,
    collectionBlocksSlug,
    setViewMode,
    setEditingSlug,
    setDraftType,
    setEditCollectionSlug,
    setCollectionBlocksSlug,
    resetView,
    showCopyBlockModal,
    setShowCopyBlockModal,
    editorReturnView,
    setEditorReturnView,
  } = useContentViewStore();

  const { useDeleteCollection, useDeleteBlock } = useContentQueries();
  const deleteCollectionMutation = useDeleteCollection();
  const deleteBlockMutation = useDeleteBlock();

  const [pendingDelete, setPendingDelete] = useState<{
    kind: 'block' | 'collection';
    slug: string;
  } | null>(null);

  const handleCreateBlockByType = (type: ExploreBlockType) => {
    setDraftType(type);
    setEditingSlug(undefined);
    setEditorReturnView('list');
    setViewMode('editor');
  };

  const handleCreateCollection = () => {
    setViewMode('createCollection');
  };

  const handleEditBlock = (slug: string) => {
    setEditingSlug(slug);
    setDraftType(undefined);
    setEditorReturnView('list');
    setViewMode('editor');
  };

  const handleBackFromEditor = () => {
    if (editorReturnView === 'collectionBlocks' && collectionBlocksSlug) {
      setEditingSlug(undefined);
      setDraftType(undefined);
      setViewMode('collectionBlocks');
      return;
    }
    resetView();
  };

  const handleEditCollection = (slug: string) => {
    setEditCollectionSlug(slug);
    setViewMode('editCollection');
  };

  const handleViewCollectionBlocks = (slug: string) => {
    setCollectionBlocksSlug(slug);
    setViewMode('collectionBlocks');
  };

  const handleBackToList = () => {
    resetView();
  };

  const handleSaveSuccess = () => {
    handleBackToList();
  };

  const deleteCollection = async (slug: string) => {
    try {
      await deleteCollectionMutation.mutateAsync(slug);
      toast.success('Collection deleted successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete collection');
    }
  };

  const deleteBlock = async (slug: string) => {
    try {
      await deleteBlockMutation.mutateAsync(slug);
      toast.success('Block deleted successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete block');
    }
  };

  const handleDeleteCollection = (slug: string) => setPendingDelete({ kind: 'collection', slug });
  const handleDeleteBlock = (slug: string) => setPendingDelete({ kind: 'block', slug });

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    const { kind, slug } = pendingDelete;
    setPendingDelete(null);
    if (kind === 'block') {
      deleteBlock(slug);
    } else {
      deleteCollection(slug);
    }
  };

  const deleteConfirmModal = (
    <ConfirmModal
      isOpen={!!pendingDelete}
      title={pendingDelete?.kind === 'collection' ? 'Delete collection' : 'Delete block'}
      message={
        pendingDelete?.kind === 'block'
          ? `"${pendingDelete.slug}" will be deleted permanently and removed from every collection.`
          : `"${pendingDelete?.slug}" will be deleted permanently.`
      }
      confirmLabel="Yes, delete"
      onConfirm={handleConfirmDelete}
      onClose={() => setPendingDelete(null)}
    />
  );

  const handleCopyBlock = async (slug: string) => {
    useContentViewStore.setState({ showCopyBlockModal: true, copyBlockSlug: slug });
  };

  switch (viewMode) {
    case 'list':
      return (
        <>
          <CopyBlockModal
            isPopupOpen={showCopyBlockModal}
            closePopup={() => setShowCopyBlockModal(false)}
          />
          {deleteConfirmModal}

          <ContentListView
            onCreateCollection={handleCreateCollection}
            onCreateBlock={handleCreateBlockByType}
            onEditBlock={handleEditBlock}
            onEditCollection={handleEditCollection}
            onViewCollectionBlocks={handleViewCollectionBlocks}
            onDeleteCollection={handleDeleteCollection}
            onDeleteBlock={handleDeleteBlock}
            onCopyBlock={handleCopyBlock}
          />
        </>
      );

    case 'editor':
      return (
        <ContentEditorView
          editingSlug={editingSlug}
          draftType={draftType}
          onBack={handleBackFromEditor}
          onSaveSuccess={handleBackFromEditor}
        />
      );

    case 'createCollection':
      return <CreateCollectionView onBack={handleBackToList} onSaveSuccess={handleSaveSuccess} />;

    case 'editCollection':
      if (!editCollectionSlug) {
        return null;
      }
      return (
        <EditCollectionView
          collectionSlug={editCollectionSlug}
          onBack={handleBackToList}
          onSaveSuccess={handleSaveSuccess}
        />
      );

    case 'collectionBlocks':
      if (!collectionBlocksSlug) {
        return null;
      }
      return (
        <>
          <CopyBlockModal
            isPopupOpen={showCopyBlockModal}
            closePopup={() => setShowCopyBlockModal(false)}
          />
          {deleteConfirmModal}

          <CollectionBlocksView
            collectionSlug={collectionBlocksSlug}
            onBack={handleBackToList}
            onDeleteBlock={handleDeleteBlock}
            onCopyBlock={handleCopyBlock}
          />
        </>
      );

    default:
      return null;
  }
};

export default ContentManager;
